import React, { useEffect, useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Send, MessageCircleHeart } from "lucide-react";
import PaywallCard from "@/components/PaywallCard";

const SUGGESTIONS = [
  "Where do I start if my parent has died?",
  "How do I use Tell Us Once?",
  "What is probate and do I need it?",
  "How do I write a letter to my children in case I die?",
];

export default function Chat() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [session] = useState(`session-${Date.now()}`);
  const endRef = useRef(null);

  useEffect(() => {
    if (!user?.is_premium) return;
    api.get(`/chat/history?session_id=${encodeURIComponent(session)}`)
       .then((r) => setMessages(r.data.messages || []))
       .catch(() => {});
  }, [session, user]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API_BASE}/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: session, message: msg }),
      });
      if (!res.ok || !res.body) {
        const err = await res.text().catch(() => "");
        setMessages((m) => {
          const c = [...m]; c[c.length - 1] = { role: "assistant", content: `I'm having trouble responding right now. ${err}` }; return c;
        });
        setStreaming(false);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const chunk of parts) {
          const line = chunk.replace(/^data: /, "");
          if (!line.trim()) continue;
          try {
            const j = JSON.parse(line);
            if (j.delta) {
              setMessages((m) => {
                const c = [...m];
                c[c.length - 1] = { role: "assistant", content: (c[c.length - 1].content || "") + j.delta };
                return c;
              });
            } else if (j.error) {
              setMessages((m) => {
                const c = [...m];
                c[c.length - 1] = { role: "assistant", content: `Sorry — ${j.error}` };
                return c;
              });
            }
          } catch (e) { console.error("Chat SSE parse error:", e); }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const c = [...m]; c[c.length - 1] = { role: "assistant", content: "Network error. Please try again in a moment." }; return c;
      });
    } finally { setStreaming(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-10 md:py-14">
      <div className="text-xs uppercase tracking-[0.2em] text-[#6B7F62] mb-3">Compassionate assistant</div>
      <h1 className="font-serif text-4xl md:text-5xl text-[#2D332D] mb-2">Ask anything</h1>
      <p className="text-[#576056] mb-8 max-w-xl">
        No question is too small. Practical or emotional — I'll do my best to help in plain, UK-focused English.
      </p>

      {!user?.is_premium ? (
        <PaywallCard
          title="The AI companion is a Premium feature."
          subtitle="A warm, private place to ask anything — practical, legal, emotional. Trained specifically on UK bereavement admin, using Claude."
          features={[
            "Ask anything — probate, benefits, Tell Us Once, LPAs",
            "Warm, plain-English replies (no jargon)",
            "Also unlocks premium chapters + Plan Builder",
            "One-time £29, lifetime access",
          ]}
        />
      ) : (
      <div className="card overflow-hidden flex flex-col" style={{ minHeight: "60vh" }}>
        <div className="bg-[#F3F1ED] px-6 py-4 border-b border-[#EAE6E1] flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#E9ECE8] flex items-center justify-center text-[#6B7F62]">
            <MessageCircleHeart size={18} />
          </div>
          <div>
            <div className="font-serif text-lg text-[#2D332D] leading-tight">At The End of The Day</div>
            <div className="text-xs text-[#8A9488]">General guidance, not legal advice</div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="space-y-3">
              <p className="text-[#576056]">Try one of these to begin:</p>
              <div className="flex flex-wrap gap-2">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    data-testid={`chat-suggestion-${i}`}
                    onClick={() => send(s)}
                    className="text-sm bg-white border border-[#EAE6E1] hover:border-[#9EAD94] px-4 py-2 rounded-full text-[#2D332D] transition-colors"
                  >{s}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[85%] px-5 py-3.5 rounded-2xl whitespace-pre-wrap leading-relaxed ${
                  m.role === "user"
                    ? "bg-[#6B7F62] text-white rounded-tr-sm"
                    : "bg-[#F5ECE7] text-[#2D332D] rounded-tl-sm"
                }`}
              >
                {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="border-t border-[#EAE6E1] p-4 bg-white flex items-end gap-3"
          data-testid="chat-form"
        >
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask a question…"
            rows={1}
            className="input resize-none flex-1"
            data-testid="chat-input"
            disabled={streaming}
          />
          <button
            type="submit"
            disabled={streaming || !input.trim()}
            className="btn-primary px-5"
            data-testid="chat-send"
          >
            <Send size={16} />
          </button>
        </form>
      </div>
      )}
    </div>
  );
}
